import { Box, Typography, Paper, Grid, Card, CardContent, Avatar, Chip, Button, List, ListItem, ListItemAvatar, ListItemText, Divider, Container, Alert, LinearProgress } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useAppSelector } from '../hooks/redux'
import { useHasRole } from '../hooks/usePermission'
import { useNavigate } from 'react-router-dom'
import {
  Folder,
  Groups,
  People,
  Description,
  Assessment,
  VideoCall,
  TrendingUp,
  Notifications,
  Schedule,
  ArrowForward,
  AttachFile,
  BarChart,
  CalendarToday,
  AccessTime,
} from '@mui/icons-material'
import { gradientBrand } from '../theme/brand'
import NextHearingCountdown from '../components/NextHearingCountdown'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetReportsDashboardQuery } from '../store/api/reportsApi'
import { getPriorityRegistrationIssues, getTeamRegistrationSummary } from '../utils/teamRegistration'

const getActivityIcon = (type: string) => {
  switch (type) {
    case 'memorial':
      return <Description color="primary" />
    case 'score':
      return <Assessment color="success" />
    case 'courtroom':
      return <VideoCall color="info" />
    case 'team':
      return <Groups color="secondary" />
    case 'evidence':
      return <AttachFile color="warning" />
    default:
      return <Notifications color="action" />
  }
}

export default function HomePage() {
  const { user } = useAppSelector((state) => state.auth)
  const isAdminQueue = useHasRole(['secretary', 'support'])
  const navigate = useNavigate()
  const { data: teams = [] } = useGetTeamsQuery()
  const { data: cases = [] } = useGetCasesQuery()
  const { data: dashboard } = useGetReportsDashboardQuery(undefined, {
    skip: !user || !isAdminQueue,
  })
  const realStats = {
    totalCases: cases.length,
    totalTeams: teams.length,
    totalParticipants: teams.reduce((sum, t) => sum + (t.members?.filter(Boolean).length || 0), 0),
    totalCourtrooms: dashboard?.total_courtrooms ?? 0,
    submittedScoresCount: dashboard?.submitted_scores_count ?? 0,
    averageScore:
      dashboard?.average_total_score != null
        ? Number(dashboard.average_total_score.toFixed(2))
        : null,
  }
  const userTeam = teams.find((team) => team.members.includes(user?.id || ''))
  const userTeamSummary = userTeam ? getTeamRegistrationSummary(userTeam) : null
  const userTeamIssues = userTeam ? getPriorityRegistrationIssues(userTeam, 5) : []
  const canViewAdminQueue = isAdminQueue
  const teamsNeedingAction = teams.filter((team) => {
    const summary = getTeamRegistrationSummary(team)
    return team.status !== 'deleted' && (!summary.is_complete || team.status === 'registered' || team.status === 'rejected')
  })
  const missingStudentIdCount = teams.filter((team) => getTeamRegistrationSummary(team).missing_keys.includes('missing_student_id')).length
  const missingProfileCount = teams.filter((team) => getTeamRegistrationSummary(team).missing_keys.includes('team_profile')).length
  const incompleteMembersCount = teams.filter((team) => getTeamRegistrationSummary(team).missing_keys.includes('member_count')).length
  const uploadedProfileCount = teams.filter((team) => getTeamRegistrationSummary(team).has_profile_file).length

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: 'Quản trị viên',
      secretary: 'Ban Thư ký',
      judge: 'Giám khảo',
      student: 'Thí sinh',
      support: 'Hỗ trợ kỹ thuật',
    }
    return labels[role] || role
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 4 } }}>
      {/* Welcome — thẻ hero đồng bộ nhận diện */}
      <Box
        sx={(theme) => ({
          mb: 4,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.08)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            background: gradientBrand,
            borderRadius: '3px 0 0 3px',
          },
        })}
      >
        <Box sx={{ pl: { xs: 0.5, sm: 1 } }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.65rem', sm: '2.125rem' }, color: 'primary.main' }}>
            Chào mừng đến với E-Court Platform
          </Typography>
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                Xin chào, <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{user.full_name || user.username}</Box>
              </Typography>
              <Chip label={getRoleLabel(user.role)} color="secondary" variant="outlined" size="small" sx={{ fontWeight: 600 }} />
            </Box>
          )}
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1.25, maxWidth: '42rem', lineHeight: 1.65 }}>
            Hệ thống quản lý và tổ chức cuộc thi tranh tụng điện tử — giao diện thân thiện, luồng làm việc rõ ràng.
          </Typography>
        </Box>
      </Box>

      {userTeam && userTeamSummary && !userTeamSummary.is_complete && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 2, alignItems: 'flex-start' }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/teams')}>
              Bổ sung
            </Button>
          }
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Hồ sơ đội "{userTeam.name}" còn thiếu {userTeamSummary.missing_count} mục
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <LinearProgress
              variant="determinate"
              value={(userTeamSummary.required_done / Math.max(1, userTeamSummary.required_total)) * 100}
              color="warning"
              sx={{ maxWidth: 320, height: 7, borderRadius: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {userTeamIssues.map((item) => (
                <Chip key={item.key} label={item.label} color="warning" size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        </Alert>
      )}

      {canViewAdminQueue && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                Hàng chờ duyệt hồ sơ đội
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {teamsNeedingAction.length} đội cần kiểm tra, duyệt hoặc yêu cầu bổ sung.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`Thiếu MSSV: ${missingStudentIdCount}`} color={missingStudentIdCount ? 'warning' : 'default'} size="small" />
              <Chip label={`Thiếu hồ sơ đội: ${missingProfileCount}`} color={missingProfileCount ? 'warning' : 'default'} size="small" />
              <Chip label={`Chưa đủ thành viên: ${incompleteMembersCount}`} color={incompleteMembersCount ? 'warning' : 'default'} size="small" />
              <Chip label={`Đã upload profile: ${uploadedProfileCount}`} color="success" variant="outlined" size="small" />
            </Box>
            <Button variant="outlined" size="small" onClick={() => navigate('/teams')} sx={{ textTransform: 'none' }}>
              Mở đội thi
            </Button>
          </Box>
        </Paper>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              borderTop: '4px solid',
              borderTopColor: 'primary.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.primary.main, 0.12)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Tổng số cuộc thi
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {realStats.totalCases}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha('#144E8C', 0.12), color: 'primary.main', width: 56, height: 56 }}>
                  <Folder />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
              borderTop: '4px solid',
              borderTopColor: 'secondary.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.secondary.main, 0.15)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Tổng số đội thi
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="secondary.main">
                    {realStats.totalTeams}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha('#F64183', 0.12), color: 'secondary.main', width: 56, height: 56 }}>
                  <Groups />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
              borderTop: '4px solid',
              borderTopColor: 'success.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.success.main, 0.14)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Tổng số thí sinh
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {realStats.totalParticipants}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: (t) => alpha(t.palette.success.main, 0.12), color: 'success.dark', width: 56, height: 56 }}>
                  <People />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
              borderTop: '4px solid',
              borderTopColor: 'info.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.info.main, 0.14)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Điểm trung bình
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {(realStats.averageScore ?? '—')}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: (t) => alpha(t.palette.info.main, 0.12), color: 'info.dark', width: 56, height: 56 }}>
                  <TrendingUp />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={(theme) => ({
              p: 3,
              height: '100%',
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
            })}
          >
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
              <ArrowForward color="secondary" /> Truy cập nhanh
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Folder />}
                onClick={() => navigate('/cases')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Cuộc thi
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Groups />}
                onClick={() => navigate('/teams')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Đội thi
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Description />}
                onClick={() => navigate('/memorials')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Memorial
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Assessment />}
                onClick={() => navigate('/scoring')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Chấm điểm
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<VideoCall />}
                onClick={() => navigate('/courtroom')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Virtual Courtroom
              </Button>
              {isAdminQueue && (
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<BarChart />}
                  onClick={() => navigate('/reports')}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
                >
                  Báo cáo và Phân tích
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Recent Activities */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={(theme) => ({
              p: 3,
              height: '100%',
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
            })}
          >
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
              <Notifications color="secondary" /> Hoạt động gần đây
            </Typography>
            <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 2 }}>
              Tính năng nhật ký hoạt động real-time sẽ được bổ sung trong giai đoạn tiếp theo.
              Trong khi đó, hãy kiểm tra mục Thông báo để xem các sự kiện mới nhất liên quan đến bạn.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Notifications />}
              onClick={() => navigate('/notifications')}
              sx={{ textTransform: 'none' }}
            >
              Xem thông báo
            </Button>
          </Paper>
        </Grid>

        {/* Upcoming Courtrooms & Top Teams */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* T-7 countdown widget — phiên Bán kết / Chung kết tiếp theo */}
            <NextHearingCountdown />

            {/* Upcoming Courtrooms */}
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 3,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
              })}
            >
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                <Schedule color="secondary" /> Sắp diễn ra
              </Typography>
              <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 1 }}>
                Lịch phiên Bán kết / Chung kết hiển thị qua widget T-7 ở trên — countdown
                tự động theo dữ liệu thực tế của cuộc thi bạn được phân công.
              </Typography>
              <Button
                fullWidth
                variant="text"
                color="primary"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/courtroom')}
                sx={{ mt: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Xem tất cả
              </Button>
            </Paper>

            {/* Top Teams */}
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 3,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
              })}
            >
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                <TrendingUp color="secondary" /> Top 3 đội thi
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(dashboard?.leaderboard || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    Chưa có đội nào có phiếu điểm — sẽ hiển thị sau khi giám khảo gửi điểm chính thức.
                  </Typography>
                ) : (
                  (dashboard?.leaderboard || []).slice(0, 3).map((team, idx) => {
                    const rank = idx + 1
                    return (
                      <Box
                        key={team.team_id}
                        sx={(theme) => ({
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor:
                            rank === 1 ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.action.hover, 0.5),
                          border: rank === 1 ? `1px solid ${alpha(theme.palette.secondary.main, 0.35)}` : '1px solid transparent',
                        })}
                      >
                        <Chip
                          label={rank}
                          color={rank === 1 ? 'primary' : 'default'}
                          sx={{ fontWeight: 'bold', minWidth: 40 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {team.team_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {team.score_count} phiếu
                          </Typography>
                        </Box>
                        <Typography variant="h6" fontWeight={600} color="primary.main">
                          {team.avg_score?.toFixed(2)}
                        </Typography>
                      </Box>
                    )
                  })
                )}
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Container>
  )
}
